import { runtimeConfig } from '@/shared/config'
import { ApiError } from './errors'

// HTTP-линия хаба. Cookie-auth: credentials:'include' гонит httpOnly hub-session
// cookie (Domain=.stit.tech) в каждый запрос — cross-origin к id.stit.tech и
// api.hub.stit.tech. Два бэкенда → два корня API. Это тонкий seam: когда появятся
// openapi.json обоих сервисов, тело request заменяется на сгенерированные
// typed-клиенты (Hey API), сигнатуры не меняются.

// company-backend: API_BASE_URL уже включает корень /api (дефолт '/api').
const COMPANY_BASE = runtimeConfig.API_BASE_URL
// employee-backend: ID_BASE_URL — origin (id.stit.tech), API под /api.
const EMPLOYEE_BASE = `${runtimeConfig.ID_BASE_URL}/api`
// Контур заказчика — тот же origin company-backend, но ВНЕ /api
// (/auth/customer/*), поэтому база = API_BASE_URL без хвостового /api. Отдельную
// env не заводим: значение выводится однозначно, а лишняя переменная в compose
// — ещё один способ разъехаться.
const CUSTOMER_BASE = COMPANY_BASE.replace(/\/api\/?$/, '')

let onUnauthorized: (() => void) | null = null

// 401 (сессия истекла/отсутствует) обрабатывается сверху: app инжектит сброс
// session-кэша. shared не может импортировать app (FSD) — сеттер.
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  onUnauthorized = handler
}

// Хаб держит access-cookie короткой, а живость сессии — в refresh-cookie
// (host-only на id.stit.tech, Path=/auth, 00-architecture § 3). Обе httpOnly, так
// что из JS не видно ни срока access, ни наличия refresh: единственный способ
// отличить «протухла минуту назад» от «сессии нет» — сходить в /auth/refresh.
let refreshInFlight: Promise<boolean> | null = null

function refreshSession(): Promise<boolean> {
  // Refresh ротирующий: параллельные попытки погасили бы друг друга, поэтому все
  // 401, пришедшие одновременно, ждут один и тот же запрос.
  refreshInFlight ??= fetch(`${runtimeConfig.ID_BASE_URL}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
  })
    .then((res) => res.ok)
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null
    })
  return refreshInFlight
}

let customerRefreshInFlight: Promise<boolean> | null = null

// Контур заказчика ротируется своей цепочкой (cookie stit_customer_refresh,
// Path=/auth/customer) и своим стором — общий с сотрудником refresh не годится.
function refreshCustomerSession(): Promise<boolean> {
  customerRefreshInFlight ??= fetch(`${CUSTOMER_BASE}/auth/customer/refresh`, {
    method: 'POST',
    credentials: 'include',
  })
    .then((res) => res.ok)
    .catch(() => false)
    .finally(() => {
      customerRefreshInFlight = null
    })
  return customerRefreshInFlight
}

// Контур сессии: чем продлевать и что делать, когда 401 пережил продление.
interface SessionLine {
  refresh: () => Promise<boolean>
  onExpired?: (() => void) | undefined
}

const employeeLine: SessionLine = {
  refresh: refreshSession,
  get onExpired() {
    return onUnauthorized ?? undefined
  },
}

// Заказчик живёт вне employee-кэша: сброс hub-сессии здесь был бы враньём.
// Протухание ловят сами экраны /customer/* (me → null).
const customerLine: SessionLine = { refresh: refreshCustomerSession }

type QueryValue = string | number | boolean | undefined | null

export interface RequestOpts {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'
  path?: string
  query?: Record<string, QueryValue>
  body?: unknown
  // Логин-ручки: 401 там значит «креды не подошли», а не «сессия протухла» —
  // продлевать нечего, и лишний refresh только путает картину в сети.
  noRefresh?: boolean
}

function buildUrl(base: string, path: string, query?: Record<string, QueryValue>): string {
  const url = `${base}${path}`
  if (!query) return url
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== null && value !== '') qs.set(key, String(value))
  }
  const suffix = qs.toString()
  return suffix ? `${url}?${suffix}` : url
}

function send(base: string, opts: RequestOpts): Promise<Response> {
  const hasBody = opts.body !== undefined
  return fetch(buildUrl(base, opts.path ?? '', opts.query), {
    method: opts.method ?? 'GET',
    credentials: 'include',
    ...(hasBody ? { headers: { 'Content-Type': 'application/json' } } : {}),
    ...(hasBody ? { body: JSON.stringify(opts.body) } : {}),
  })
}

async function request<T>(base: string, opts: RequestOpts, line: SessionLine): Promise<T> {
  let res = await send(base, opts)
  // Одна попытка: refresh либо продлевает сессию (тогда повтор проходит), либо
  // она кончилась и второй заход даст тот же 401.
  if (res.status === 401 && !opts.noRefresh && (await line.refresh())) {
    res = await send(base, opts)
  }
  if (res.status === 401) line.onExpired?.()
  if (res.status === 204) {
    if (!res.ok) throw ApiError.from(res.status, undefined)
    return undefined as T
  }
  const data: unknown = await res.json().catch(() => undefined)
  if (!res.ok) throw ApiError.from(res.status, data)
  return data as T
}

// Запрос к employee-backend (id.stit.tech): /api/employees/*.
export function employeeRequest<T>(opts: RequestOpts): Promise<T> {
  return request<T>(EMPLOYEE_BASE, opts, employeeLine)
}

// Запрос к company-backend (api.hub.stit.tech): companies/projects/visibility/search/tags.
export function companyRequest<T>(opts: RequestOpts): Promise<T> {
  return request<T>(COMPANY_BASE, opts, employeeLine)
}

// Контур заказчика (00-architecture § 3): /auth/customer/* на том же origin, что
// company-backend, но вне /api. Своя cookie, своя ротация, свой набор прав.
export function customerRequest<T>(opts: RequestOpts): Promise<T> {
  return request<T>(CUSTOMER_BASE, opts, customerLine)
}
